import { useMemo } from 'react'

import { AttentionTable, TopUsersTable } from './activity-user-tables'
import { useActivityParams } from './use-activity-params'
import { DrawerUser, UserActivityDrawer } from './user-activity-drawer'

import {
  ActivityBarChart,
  BarDatum,
  ChartLegend
} from '@/components/activity/activity-bar-chart'
import { ActivityHeatmap } from '@/components/activity/activity-heatmap'
import {
  ActivityHourGrid,
  formatHourPeak
} from '@/components/activity/activity-hour-grid'
import {
  ActivityPanel,
  DeltaText,
  ErrorNote,
  GranularitySwitch,
  KpiTile,
  Pulse
} from '@/components/activity/activity-panel'
import {
  useQueryGetOrgActivity,
  useQueryGetOrgActivityByHour,
  useQueryGetOrgActivityHeatmap
} from '@/services'
import {
  ACTIVITY_FOOTER_TEXT,
  diffDays,
  formatNumber,
  formatPercent,
  formatPeriodLong,
  formatPeriodShort,
  formatSeatsHint,
  getPeriodRange,
  getSeats,
  GRANULARITY_UNIT
} from '@/utils/activity'

type OrgActivityTabProps = {
  orgId: string
  orgName?: string | null
}

const TOP_LIMIT = 10

export const OrgActivityTab = ({ orgId, orgName }: OrgActivityTabProps) => {
  const {
    granularity,
    period,
    user,
    userTab,
    setGranularity,
    setPeriod,
    openUser,
    closeUser,
    setUserTab
  } = useActivityParams()

  const activity = useQueryGetOrgActivity(orgId, {
    granularity,
    top_limit: TOP_LIMIT
  })
  const heatmap = useQueryGetOrgActivityHeatmap(orgId)
  const hourly = useQueryGetOrgActivityByHour(orgId)

  const data = activity.data
  const summary = data?.summary

  // selected bucket -> top users of that bucket (clipped to the resolved range)
  const selectedRange = useMemo(() => {
    if (!period || !data?.series.some((p) => p.period_start === period))
      return undefined

    const range = getPeriodRange(period, granularity)

    return { from: range.from, to: range.to > data.to ? data.to : range.to }
  }, [period, granularity, data])

  const periodActivity = useQueryGetOrgActivity(
    orgId,
    { granularity, top_limit: TOP_LIMIT, ...selectedRange },
    { enabled: !!selectedRange }
  )

  const topUsersSource = selectedRange ? periodActivity : activity
  const topRange =
    selectedRange ?? (data ? { from: data.from, to: data.to } : undefined)
  const rangeDays = topRange ? diffDays(topRange.from, topRange.to) + 1 : 0

  const chartData: BarDatum[] = useMemo(
    () =>
      (data?.series ?? []).map((point) => ({
        key: point.period_start,
        value: point.active_users,
        label: formatPeriodShort(point.period_start, granularity),
        title: `${formatPeriodLong(point.period_start, granularity)}: ${
          point.active_users
        } active users`
      })),
    [data, granularity]
  )

  const selectedPoint = data?.series.find((p) => p.period_start === period)

  // identity of the drawer user from the loaded lists
  const drawerUser: DrawerUser | null = useMemo(() => {
    if (!user) return null

    const known = [
      ...(periodActivity.data?.top_users ?? []),
      ...(data?.top_users ?? []),
      ...(data?.attention ?? [])
    ].find((u) => u.user_id === user)

    return known ?? { user_id: user }
  }, [user, data, periodActivity.data])

  const seats = getSeats(summary)
  const hasSeats = seats !== null
  const peak = hourly.data ? formatHourPeak(hourly.data.cells) : undefined

  return (
    <section
      aria-label="Organization activity"
      className="mt-4 space-y-4 rounded-xl bg-muted/20 p-6 text-foreground"
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-0.5">
          <h2 className="text-lg font-semibold">{orgName || 'Organization'}</h2>
          <p className="text-sm text-muted-foreground/70">
            360 user activity · Org <span className="font-mono">{orgId}</span>
            {summary && ` · ${formatNumber(summary.members)} members`}
          </p>
        </div>
      </header>

      {activity.isError && (
        <ErrorNote>
          Activity could not be loaded for this organization.
        </ErrorNote>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile
          hint="360 open in the last 15 min"
          indicator="live"
          isLoading={activity.isLoading}
          label="Active now"
          value={formatNumber(summary?.active_now)}
        />
        <KpiTile
          hint="distinct users since midnight (Berlin)"
          isLoading={activity.isLoading}
          label="Active today"
          value={formatNumber(summary?.active_today)}
        />
        <KpiTile
          hint={
            summary && (
              <DeltaText
                current={summary.active_7d}
                previous={summary.active_prev_7d}
                suffix="vs previous 7 days"
              />
            )
          }
          isLoading={activity.isLoading}
          label="Active 7 days"
          value={formatNumber(summary?.active_7d)}
        />
        <KpiTile
          hint={
            summary &&
            (hasSeats
              ? formatSeatsHint(summary)
              : 'No seats known · rate vs members')
          }
          isLoading={activity.isLoading}
          label="Seat activation · 30d"
          suffix={
            summary &&
            (hasSeats
              ? `${formatNumber(summary.active_30d)} / ${formatNumber(
                  seats
                )} seats`
              : `${formatNumber(summary.active_30d)} / ${formatNumber(
                  summary.members
                )} members`)
          }
          value={formatPercent(
            hasSeats
              ? summary?.activation_rate_30d
              : summary?.member_activation_rate_30d
          )}
        />
        <KpiTile
          hint={
            summary &&
            `${formatNumber(summary.dormant_30d)} dormant 30d+ · ${formatNumber(
              summary.never_seen
            )} never seen`
          }
          isLoading={activity.isLoading}
          label="Needs attention"
          tone={
            summary && summary.dormant_30d + summary.never_seen > 0
              ? 'warning'
              : 'default'
          }
          value={formatNumber(
            summary ? summary.dormant_30d + summary.never_seen : undefined
          )}
        />
      </div>

      {/* active users over time */}
      <ActivityPanel
        actions={
          <>
            <ChartLegend
              items={[
                { label: 'active users', kind: 'bar' },
                { label: 'members', kind: 'dashed' }
              ]}
            />
            <GranularitySwitch onChange={setGranularity} value={granularity} />
          </>
        }
        subtitle={`Distinct members active per ${
          GRANULARITY_UNIT[granularity]
        }${
          data
            ? ` · ${data.series.length} ${GRANULARITY_UNIT[granularity]}s`
            : ''
        } · click a bar to see its top users`}
        title="Active users over time"
      >
        {activity.isLoading ? (
          <Pulse className="h-[220px] w-full" />
        ) : (
          <ActivityBarChart
            ariaLabel="Active users over time"
            data={chartData}
            onSelect={setPeriod}
            referenceValue={summary?.members}
            selectedKey={selectedPoint?.period_start}
          />
        )}
        {selectedPoint && summary && (
          <p className="text-sm text-muted-foreground/80">
            Showing{' '}
            <span className="font-semibold text-foreground">
              {formatPeriodLong(selectedPoint.period_start, granularity)}
            </span>
            : {selectedPoint.active_users} of {summary.members} members active{' '}
            <button
              className="ml-2 text-secondary underline-offset-4 hover:underline"
              onClick={() => setPeriod(null)}
              type="button"
            >
              Clear
            </button>
          </p>
        )}
      </ActivityPanel>

      {/* heatmap */}
      <ActivityPanel
        subtitle="Active members per day · last 12 months"
        title="Activity heatmap"
      >
        {heatmap.data ? (
          <ActivityHeatmap
            ariaLabel="Active members per day in the last 12 months"
            days={heatmap.data.days}
            from={heatmap.data.from}
            to={heatmap.data.to}
          />
        ) : heatmap.isError ? (
          <ErrorNote>Heatmap could not be loaded.</ErrorNote>
        ) : (
          <Pulse className="h-28 w-full" />
        )}
      </ActivityPanel>

      {/* by hour */}
      <ActivityPanel
        actions={
          peak && (
            <span className="text-xs tabular-nums text-muted-foreground/80">
              {peak}
            </span>
          )
        }
        subtitle="Last 28 complete days · Europe/Berlin · organization level only"
        title="Average active users by hour"
      >
        {hourly.data ? (
          <ActivityHourGrid cells={hourly.data.cells} />
        ) : hourly.isError ? (
          <ErrorNote>Hourly activity could not be loaded.</ErrorNote>
        ) : (
          <Pulse className="h-40 w-full" />
        )}
      </ActivityPanel>

      {/* users */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ActivityPanel
          subtitle={`Most active days · ${
            selectedPoint
              ? formatPeriodLong(selectedPoint.period_start, granularity)
              : `last ${data?.series.length ?? ''} ${
                  GRANULARITY_UNIT[granularity]
                }s`
          }`}
          title="Top users"
        >
          <TopUsersTable
            isLoading={topUsersSource.isLoading}
            onSelectUser={openUser}
            rangeDays={rangeDays}
            users={topUsersSource.data?.top_users}
          />
        </ActivityPanel>
        <ActivityPanel
          subtitle="Members without activity in the last 30 days"
          title="Needs attention"
        >
          <AttentionTable
            isLoading={activity.isLoading}
            onSelectUser={openUser}
            users={data?.attention}
          />
        </ActivityPanel>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground/70">
        <span>{ACTIVITY_FOOTER_TEXT}</span>
        <span className="tabular-nums">
          Snapshot {new Date(data?.generated_at ?? Date.now()).toLocaleString()}
        </span>
      </footer>

      <UserActivityDrawer
        onClose={closeUser}
        onTabChange={setUserTab}
        orgId={orgId}
        tab={userTab}
        user={drawerUser}
      />
    </section>
  )
}
